<template>
  <div class="card shadow-sm h-100">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-6">
        <h2 class="card-title mb-0">{{ title }}</h2>
      </div>
      <div class="chart-container" style="height: 360px">
        <canvas :id="chartId" :key="chartKey"></canvas>
      </div>
      <div class="d-flex justify-content-center gap-8 mt-6">
        <div class="text-center">
          <div class="fw-bolder fs-2">{{ $t('qvct_survey.survey_report.total_responses') || 'Répondants' }}</div>
          <div class="fs-2">{{ safeRespondents }}</div>
        </div>
        <div class="text-center">
          <div class="fw-bolder fs-2">{{ $t('qvct_survey.survey_report.total_participants') || 'Participants' }}</div>
          <div class="fs-2">{{ safeParticipants }}</div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script setup>
import { computed, nextTick, onUnmounted, watch, ref } from 'vue'
import Chart from 'chart.js/auto'
import {useI18n} from "vue-i18n";
const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  totalRespondents: { type: Number, required: true },
  totalParticipants: { type: Number, required: true },
  title: { type: String, default: 'Taux de réponse' }
})

const safeRespondents = computed(() => Math.max(0, Number(props.totalRespondents) || 0))
const safeParticipants = computed(() => Math.max(0, Number(props.totalParticipants) || 0))
const answeredPercent = computed(() => {
  const p = safeParticipants.value
  return p > 0 ? Math.round((safeRespondents.value * 100) / p) : 0
})

const chart = ref(null)
const chartId = 'respondent-participation-chart'
const chartKey = computed(() => `${safeRespondents.value}-${safeParticipants.value}`)

const getChartData = () => {
  const answered = Math.min(safeRespondents.value, safeParticipants.value)
  const remaining = Math.max(safeParticipants.value - answered, 0)
  return {
    labels: [
      `${t('qvct_survey.survey_report.total_responses')} : ${answered}`,
      `${t('qvct_survey.survey_report.total_no_responses')} : ${remaining}`
    ],
    datasets: [
      {
        cutout: '60%',
        label: 'Effectifs',
        data: [answered, remaining],
        backgroundColor: ['#1c86dc', '#bcbaba'],
        borderWidth: 0
      }
    ]
  }
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right' },
    tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}` } }
  }
}

const destroyChart = () => {
  if (chart.value) {
    try { chart.value.destroy() } catch (e) {}
    chart.value = null
  }
}

const init = () => {
  destroyChart()
  nextTick(() => {
    const canvas = document.getElementById(chartId)
    if (!canvas) return
    try { const existing = Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
    const data = getChartData()
    try { chart.value = new Chart(canvas.getContext('2d'), { type: 'doughnut', data, options }) } catch (e) { console.error(e) }
  })
}

watch([safeRespondents, safeParticipants], () => { init() }, { immediate: true })
onUnmounted(() => { destroyChart() })
</script>

<style scoped>
.chart-container { position: relative; width: 70%;margin-left: 300px}
</style>


